import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const frejus: ChauffeurPage = {
  slug: "frejus",
  title: "Chauffeur privé à Fréjus – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Fréjus : transferts aéroport de Nice (~1 h), navette gare TGV de Saint-Raphaël, mise à disposition de la cité romaine aux plages, dans le Var, 7j/7.",
  keywords: [
    "chauffeur privé fréjus",
    "vtc fréjus",
    "vtc frejus var",
    "transfert fréjus aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Fréjus",
    subtitle:
      "Cité romaine du Var, des arènes à la Base Nature et à Port-Fréjus : transferts aéroport de Nice (~1 h) et navette gare TGV, 24h/24.",
    keywords: [
      "VTC Fréjus",
      "chauffeur cité romaine",
      "transfert aéroport Nice",
      "gare TGV Saint-Raphaël",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Fréjus, cité romaine du Var",
    badges: [
      "Aéroport de Nice ~1 h",
      "Gare TGV Saint-Raphaël",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Fréjus",
    items: [
      {
        title: "De la cité romaine aux plages",
        description:
          "Arènes, groupe épiscopal et Porte des Gaules d’un côté, Fréjus-Plage et Port-Fréjus de l’autre : le chauffeur relie ville haute et bord de mer sans stationnement.",
      },
      {
        title: "Aéroport de Nice en ~1 h",
        description:
          "Environ une heure par l’A8, plus loin que Cannes : marge anticipée et suivi de vol systématiques.",
      },
      {
        title: "Gare TGV à deux pas",
        description:
          "La gare de Saint-Raphaël-Valescure, voisine, met le TGV à quelques minutes : navette dédiée depuis Fréjus.",
      },
      {
        title: "Espace et grands groupes",
        description:
          "Base Nature, événements sportifs et séminaires : van et minibus VIP disponibles, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Fréjus",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Fréjus ⇄ aéroport de Nice Côte d’Azur en ~1 h par l’A8, avec suivi de vol et marge intégrée.",
      },
      {
        title: "Navette gare TGV de Saint-Raphaël",
        text: "Liaison rapide vers la gare voisine de Saint-Raphaël-Valescure pour vos départs et arrivées en TGV.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, entre la cité romaine, la Base Nature et Port-Fréjus.",
      },
      {
        title: "Patrimoine romain",
        text: "Arènes, théâtre, aqueduc et cathédrale : dépose au plus près des sites et reprise après la visite.",
      },
      {
        title: "Longue distance depuis Cannes/Nice",
        text: "Trajets depuis Cannes, Nice ou l’aéroport vers Fréjus et le golfe, en berline, van ou minibus VIP.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Arènes de Fréjus",
        description: "Amphithéâtre romain, en lisière de la vieille ville.",
      },
      {
        name: "Groupe épiscopal",
        description: "Cathédrale, cloître et baptistère au cœur de la cité.",
      },
      {
        name: "Base Nature François Léotard",
        description: "Grand parc de bord de mer, événements et sport.",
      },
      {
        name: "Port-Fréjus & Fréjus-Plage",
        description: "Marina et plage, restaurants et front de mer.",
      },
      {
        name: "Gare de Saint-Raphaël-Valescure",
        description: "Gare TGV voisine, à quelques minutes.",
      },
      {
        name: "Aéroport de Nice",
        description: "Terminaux 1 et 2, à ~1 h par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur dans la cité romaine du Var",
    subheading:
      "Deux mille ans d’histoire d’un côté, plages et marina de l’autre : Fréjus se parcourt sans se garer.",
    paragraphs: [
      "Ancienne Forum Julii fondée à l’époque romaine, Fréjus aligne arènes, théâtre antique, aqueduc et un remarquable groupe épiscopal, avant de descendre vers Fréjus-Plage, Port-Fréjus et la vaste Base Nature. KS Rent Services y assure vos déplacements avec chauffeur privé, de la ville haute au bord de mer, sans jamais chercher une place.",
      "L’aéroport de Nice Côte d’Azur est à environ une heure par l’A8 : comme partout dans l’ouest du Var, ce trajet est plus long que depuis Cannes, aussi nous prévoyons une marge et suivons votre vol. Pour le train, la gare TGV de Saint-Raphaël-Valescure, toute proche, sert de point de transfert privilégié.",
      "Entre deux visites — les arènes, le cloître à plafond peint, le port — le chauffeur vous dépose au plus près des sites et vous reprend à l’heure convenue, idéal aussi pour les événements de la Base Nature et les séminaires en van ou minibus VIP.",
    ],
    bullets: [
      "Aéroport de Nice en ~1 h",
      "Navette gare TGV de Saint-Raphaël",
      "Arènes, Base Nature & Port-Fréjus",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Fréjus",
    steps: [
      "Indiquez votre trajet (aéroport, gare de Saint-Raphaël, sites romains, plages) et vos horaires.",
      "Nous confirmons le véhicule, la marge pour l’aéroport de Nice et le forfait.",
      "Le chauffeur suit votre vol ou votre train et vous accueille à l’arrivée.",
      "Mise à disposition ou dépose au cœur de la cité, reprise sur appel.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Fréjus"),
    {
      q: "Combien de temps pour l’aéroport de Nice ?",
      a: "Environ 1 heure par l’A8. Fréjus étant à l’ouest de Nice, ce transfert est plus long que depuis Cannes : nous prévoyons une marge et suivons votre vol.",
    },
    {
      q: "Comment rejoindre la gare TGV depuis Fréjus ?",
      a: "La gare de Saint-Raphaël-Valescure, voisine, est à quelques minutes : nous assurons la navette dédiée vers vos départs et arrivées en TGV.",
    },
    {
      q: "Desservez-vous les arènes et les sites romains ?",
      a: "Oui, arènes, théâtre antique, aqueduc et groupe épiscopal : le chauffeur vous dépose au plus près et vous reprend après la visite.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en été et lors des événements de la Base Nature ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules pour un groupe ?",
      a: "Van 7 places et minibus VIP pour les familles, les groupes et les séminaires, en plus des berlines Mercedes Classe E et Classe S.",
    },
  ],
  relatedLinks: [
    {
      label: "Mise à disposition avec chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Chauffeur privé à Cannes",
      href: "/chauffeur/cannes",
    },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Louer une voiture Fréjus",
      href: "/location/location-voiture-frejus",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Fréjus",
    text: "Transfert aéroport de Nice, navette gare TGV ou mise à disposition dans la cité romaine : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Fréjus 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Fréjus : transferts aéroport de Nice (~1 h), navette gare TGV de Saint-Raphaël, cité romaine, Base Nature et Port-Fréjus.",
    canonical: "/chauffeur/frejus",
  },
};

export default frejus;
